/**
 * Auth Service — wraps the Odoo OTP flow for browser use.
 * Calls are routed through the CRA proxy to avoid CORS.
 */

const BASE = '';            // hits the proxy (setupProxy.js → Odoo)
const DB = 'e18_contract_portal';
const JSONRPC_ENDPOINT = '/api/jsonrpc';

const jsonrpc = async (endpoint, params) => {
  const res = await fetch(`${BASE}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'call',
      params,
      id: Math.floor(Math.random() * 10000)
    })
  });
  const data = await res.json();
  if (data.error) {
    throw new Error(
      data.error.data?.message || data.error.message || 'Odoo RPC error'
    );
  }
  return data.result;
};

/** Request a login OTP for an existing account */
export const requestLoginOTP = (email) =>
  jsonrpc(JSONRPC_ENDPOINT, {
    service: 'common',
    method: 'login_email_otp',
    args: [DB, email, 'website']
  });
  
/** Verify a login OTP and return auth data */
export const verifyLoginOTP = async (email, otp) => {
  const result = await jsonrpc(JSONRPC_ENDPOINT, {
    service: 'common',
    method: 'verify_otp',
    args: [DB, email, otp, 'website']
  });
  if (!result || result.error || result.faultCode) {
    throw new Error(result?.error || result?.faultString || 'Invalid OTP');
  }
  return {
    uid: result.uid || result.user_id || result.id,
    api_key: result.api_key || result.apiKey || result.key || null,
    name: result.name || email.split('@')[0],
    email
  };
};

/** Request a signup OTP for a new account */
export const requestSignupOTP = (email, name, profileType = 'client', tz = null) =>
  jsonrpc(JSONRPC_ENDPOINT, {
    service: 'common',
    method: 'signup_via_otp',
    args: [DB, email.trim().toLowerCase(), {
      name: name.trim(),
      profile_type: profileType,
      tz: tz || Intl.DateTimeFormat().resolvedOptions().timeZone
    }]
  });

/** Verify a signup OTP and finalise account creation */
export const verifySignupOTP = async (email, otp, profileType = 'client') => {
  const result = await jsonrpc(JSONRPC_ENDPOINT, {
    service: 'common',
    method: 'signup_verify_otp',
    args: [DB, email.trim().toLowerCase(), otp]
  });

  // Handle various success response formats from Odoo
  const userId = result?.uid || result?.user_id || result?.id || (typeof result === 'number' ? result : null);
  const isSuccess = !!result && (result !== false) && (result !== 0);

  if (!isSuccess) {
    throw new Error(result?.error || result?.faultString || 'Invalid OTP or signup failed');
  }

  // If backend returned user data, normalize it; otherwise return a simple success object
  return userId ? {
    uid: userId,
    name: result.name || email.split('@')[0],
    email
  } : { success: true };
};

/* ─── Local session helpers ─── */
const SESSION_KEY = 'xamta_auth';

export const saveSession = (user) =>
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));

export const getSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)); }
  catch { return null; }
};

export const clearSession = () => localStorage.removeItem(SESSION_KEY);
